// src/components/main/Header.js
import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
  Typography,
  GlobalStyles,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { CartDropdown } from '../shopper/ShopperCart';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import SecondaryLoader from '../loaders/SecondaryLoader';

// ---------------- Navigation Data ----------------
const PRODUCTS = [
  { label: 'Harvest 2024', year: '2024', disabled: false },
  { label: 'Harvest 2025 (Waitlist)', year: '2025', disabled: false },
  { label: 'Estate Bottles', year: null, disabled: true },
  { label: 'Collectible Magnums', year: null, disabled: true },
  { label: 'Gift Cards', route: '/shop-giftcard', disabled: false },
];
const LINKS = [
  { label: 'Heritage', route: '/our-heritage' },
  { label: 'Reserve', route: '/our-reserve' },
  { label: 'Contact', route: '/contact-us' },
];

export default function Header() {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();

  // loader
  const [loading, setLoading] = useState(false);
  // products dropdown
  const [anchorEl, setAnchorEl] = useState(null);
  const openProducts = Boolean(anchorEl);
  const handleProductsOpen = e => setAnchorEl(e.currentTarget);
  const handleProductsClose = () => setAnchorEl(null);
  // cart dropdown
  const [cartAnchor, setCartAnchor] = useState(null);
  const openCart = Boolean(cartAnchor);
  const handleCartOpen = e => setCartAnchor(e.currentTarget);
  const handleCartClose = () => setCartAnchor(null);
  // mobile drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer = open => () => setDrawerOpen(open);

  // nav button style
  const navButton = {
    color: '#fff',
    fontWeight: 300,
    fontSize: '0.875rem',
    letterSpacing: 1,
    textTransform: 'none',
    py: 1,
    px: 1.5,
    borderRadius: 0,
    '&:hover': { color: theme.palette.secondary.main, bgcolor: 'transparent' },
  };

  // delayed nav helper
  const goTo = path => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate(path);
    }, 2000);
  };

  return (
    <>
      {/* Prevent horizontal scroll and ensure full‐width */}
      <GlobalStyles
        styles={{
          'html, body, #root': { width: '100vw', overflowX: 'hidden' },
        }}
      />

      <AnimatePresence>
        {loading && <SecondaryLoader key="header-loader" />}
      </AnimatePresence>

      {/* Fixed AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backdropFilter: 'blur(14px)',
          background: '#000',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.6)',
          zIndex: theme.zIndex.appBar + 1,
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 5 }, py: 1, justifyContent: 'space-between' }}>
          {/* Logo */}
          <Box
            component="img"
            src="https://brokepiggytoken.xyz/wp-content/uploads/2025/07/from-marrakech-with-heritage-4-scaled.png"
            alt="Marrakech Reserve logo"
            onClick={() => goTo('/')}
            sx={{
              height: { xs: 40, md: 52 },
              width: 'auto',
              cursor: 'pointer',               // indicate it's clickable
            }}
          />

          {mdUp ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {/* Products menu */}
              <Button
                endIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}
                onClick={handleProductsOpen}
                sx={navButton}
              >
                <Typography variant="button" color="#fff" sx={{ fontWeight: 300 }}>
                  Products
                </Typography>
              </Button>

              {/* Links */}
              {LINKS.map(({ label, route }) => (
                <Button key={label} onClick={() => goTo(route)} sx={navButton}>
                  <Typography variant="button" color="#fff" sx={{ fontWeight: 300 }}>
                    {label}
                  </Typography>
                </Button>
              ))}

              {/* Cart icon */}
              <IconButton onClick={handleCartOpen} sx={{ color: '#fff' }}>
                <ShoppingCartOutlinedIcon />
              </IconButton>

              {/* Decorative tile */}
              <Box
                component="img"
                src="https://brokepiggytoken.xyz/wp-content/uploads/2025/07/from-marrakech-with-heritage-7-scaled.png"
                alt="Decorative tile"
                sx={{ height: 28, width: 'auto', ml: 4 }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handleCartOpen} sx={{ color: '#fff' }}>
                <ShoppingCartOutlinedIcon />
              </IconButton>
              <IconButton onClick={toggleDrawer(true)} sx={{ color: '#fff' }}>
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>

        {/* Desktop Products menu */}
        <Menu
          anchorEl={anchorEl}
          open={openProducts}
          onClose={handleProductsClose}
          TransitionComponent={Fade}
          PaperProps={{ sx: { bgcolor: '#111', color: '#fff', mt: 1, p: 1 } }}
        >
          {PRODUCTS.map(({ label, year, route, disabled }) => (
            <MenuItem
              key={label}
              disabled={disabled}
              onClick={() => {
                handleProductsClose();
                if (!disabled) {
                  year ? goTo(`/harvest/${year}`) : goTo(route);
                }
              }}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '0.875rem',
                fontWeight: disabled ? 300 : 400,
                color: '#fff',
                '&:hover': disabled ? {} : { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              {label}
            </MenuItem>
          ))}
        </Menu>

        {/* Cart dropdown */}
        <CartDropdown anchorEl={cartAnchor} open={openCart} onClose={handleCartClose} />

        {/* Mobile drawer */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
          PaperProps={{ sx: { width: 280, bgcolor: '#111', color: '#fff', p: 3 } }}
        >
          <Toolbar sx={{ px: 0, justifyContent: 'center' }}>
            <Box
              component="img"
              src="https://brokepiggytoken.xyz/wp-content/uploads/2025/07/from-marrakech-with-heritage-4-scaled.png"
              alt="Logo"
              sx={{ height: 48 }}
            />
          </Toolbar>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

          <List disablePadding>
            {PRODUCTS.map(({ label, year, route, disabled }) => (
              <ListItem key={label} disablePadding>
                <ListItemButton
                  disabled={disabled}
                  onClick={() => {
                    if (!disabled) {
                      setDrawerOpen(false);
                      year ? goTo(`/harvest/${year}`) : goTo(route);
                    }
                  }}
                  sx={{
                    pl: 3,
                    py: 1.5,
                    fontSize: '0.875rem',
                    fontWeight: disabled ? 300 : 400,
                  }}
                >
                  <ListItemText primary={label} primaryTypographyProps={{ color: '#fff' }} />
                </ListItemButton>
              </ListItem>
            ))}
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 2 }} />
            {LINKS.map(({ label, route }) => (
              <ListItem key={label} disablePadding>
                <ListItemButton
                  onClick={() => {
                    setDrawerOpen(false);
                    goTo(route);
                  }}
                  sx={{ px: 2, py: 1.5 }}
                >
                  <ListItemText primary={label} primaryTypographyProps={{ color: '#fff' }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Drawer>
      </AppBar>

      {/* Spacer so the page content sits below the fixed header */}
      <Toolbar />
    </>
  );
}
